import { Component, computed, contentChild, effect, input, model, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { PageHeaderComponent } from '../../advanced/page-header/page-header.component';
import { PageCommandComponent } from '../../misc/page-command/page-command.component';
import { PageFooterComponent } from '../../advanced/page-footer/page-footer.component';
import { PageCommandGroupComponent } from '../../misc/page-command-group/page-command-group.component';
import { NgForm } from '@angular/forms';

@Component({
  selector: 'app-form-page',
  standalone: true,
  imports: [PageHeaderComponent, PageCommandComponent, PageFooterComponent, PageCommandGroupComponent],
  templateUrl: './form-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './form-page.component.scss'
})
export class FormPageComponent {
  //Command group from content
  commandGroup = contentChild(PageCommandGroupComponent);
  

  //Inputs
  mode = model<FormMode>(FormMode.View);
  pageTitle = input.required<string>();
  allowEditMode = input<boolean>(true);
  allowNewMode = input<boolean>(true);
  form = input<NgForm>();


  //Outputs
  modeChanged = output<FormMode>();


  //Computed
  viewMode = computed(() => this.mode() === FormMode.View);
  editMode = computed(() => this.mode() === FormMode.Edit);
  newMode = computed(() => this.mode() === FormMode.New);


  //Computed actions including custom actions
  editAction = computed(() => {
    const providedCommand = this.commandGroup()?.commands().find(command => command.name === 'edit')
    return providedCommand?.action ? () => { this.onEditClick(); providedCommand.action() } : this.onEditClick;
  });

  newAction = computed(() => {
    const providedCommand = this.commandGroup()?.commands().find(command => command.name === 'new')
    return providedCommand?.action ? () => { this.onNewClick(); providedCommand.action() } : this.onNewClick;
  });

  saveAction = computed(() => {
    const providedCommand = this.commandGroup()?.commands().find(command => command.name === 'save')
    return providedCommand?.action ? () => { this.onSaveClick(); providedCommand.action() } : this.onSaveClick;
  });

  cancelAction = computed(() => {
    const providedCommand = this.commandGroup()?.commands().find(command => command.name === 'cancel')
    return providedCommand?.action ? () => { this.onCancelClick(); providedCommand.action() } : this.onCancelClick;
  });


  //Computed permissions
  editPermissions = computed(() => {
    const providedCommand = this.commandGroup()?.commands().find(command => command.name === 'edit')
    return providedCommand?.requiredPermissions?.join(',') ?? '';
  });

  newPermissions = computed(() => {
    const providedCommand = this.commandGroup()?.commands().find(command => command.name === 'new')
    return providedCommand?.requiredPermissions?.join(',') ?? '';
  });

  savePermissions = computed(() => {
    const providedCommand = this.commandGroup()?.commands().find(command => command.name === 'save')
    return providedCommand?.requiredPermissions?.join(',') ?? '';
  });


  //Custom commands
  customCommands = computed(() => {
    const commands = this.commandGroup()?.commands() ?? [];
    const knownPageCommandNames: string[] = ['new', 'edit', 'save', 'cancel'];
    return commands.filter(command => !knownPageCommandNames.includes(command.name));
  });

  
  //local state
  formValid = signal(false);


  constructor() { 
    effect(() => {
      this.modeChanged.emit(this.mode());
    });

    effect((onCleanup) => {
      const form = this.form();
      if (form) {
        const subscription = form.form.valueChanges.subscribe(() => {
          this.formValid.set(form.form.valid);
        });

        onCleanup(() => {
          subscription.unsubscribe();
        });
      }
    });

    effect(() => {
      if (this.mode() === FormMode.Edit || this.mode() === FormMode.New) {
        setTimeout(() => this.form()?.form.enable());
      }
      else {
        setTimeout(() => this.form()?.form.disable());
      }
    });
  }

  onEditClick = (): void => {
    this.mode.set(FormMode.Edit);
  }

  onSaveClick = (): void => {
    this.mode.set(FormMode.View);
  }

  onCancelClick = (): void => {
    this.mode.set(FormMode.View);
  }

  onNewClick = (): void => {
    this.mode.set(FormMode.New);
  }
}

export enum FormMode { 
  View = 'View', 
  Edit = 'Edit',
  New = 'New'
}
